import {Controller, useFieldArray, useFormContext, useWatch} from "react-hook-form";
import {Plus, Trash2} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {newSpecialSense, SENSE_LABELS, SENSE_TYPES} from "@/characters/model/CharacterSenses.ts";
import type {CharacterFormInput} from "./CharacterFormSchema.ts";

type NumberName = "senses.passivePerception" | `senses.specialSenses.${number}.rangeMeters`
    | `senses.specialSenses.${number}.rangeWhenDeafMeters`;

function SenseNumberField({name, label, optional = false, integer = false}: {
    name: NumberName; label: string; optional?: boolean; integer?: boolean;
}) {
    const {control} = useFormContext<CharacterFormInput>();
    return <Controller control={control} name={name} render={({field, fieldState}) =>
        <label className="block space-y-1 text-xs">{label}
            <Input {...field} type="number" min={integer ? 0 : 0.1} step={integer ? 1 : "any"}
                value={field.value ?? ""} placeholder={optional ? "Nicht angegeben" : undefined}
                onChange={event => field.onChange(event.target.value === "" ? (optional ? null : 0) : Number(event.target.value))}/>
            {fieldState.error && <p role="alert" className="text-destructive">{fieldState.error.message}</p>}
        </label>}/>;
}

export default function CharacterSensesForm() {
    const {control, register, setValue} = useFormContext<CharacterFormInput>();
    const senses = useFieldArray({control, name: "senses.specialSenses"});
    const values = useWatch({control, name: "senses.specialSenses"});
    return <section className="space-y-3 md:col-span-2 xl:col-span-3">
        <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Sinne</h3>
            <Button type="button" size="sm" variant="outline" onClick={() => senses.append(newSpecialSense())}>
                <Plus/> Sinn hinzufügen
            </Button>
        </div>
        <div className="max-w-xs"><SenseNumberField name="senses.passivePerception" label="Passive Wahrnehmung" optional integer/></div>
        <div className="grid gap-3 md:grid-cols-2">
            {senses.fields.map((sense, index) => <div key={sense.id} className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-2">
                    <select aria-label={`Sinn ${index + 1}`} className="h-9 grow rounded-md border border-input bg-surface px-2 text-sm"
                        {...register(`senses.specialSenses.${index}.type`, {onChange: event => {
                            if (event.target.value !== "BLINDSIGHT") {
                                setValue(`senses.specialSenses.${index}.blindOutsideRange`, false, {shouldDirty: true});
                                setValue(`senses.specialSenses.${index}.rangeWhenDeafMeters`, null, {shouldDirty: true});
                            }
                        }})}>
                        {SENSE_TYPES.map(type => <option key={type} value={type}>{SENSE_LABELS[type]}</option>)}
                    </select>
                    <Button type="button" variant="ghost" size="icon-sm" aria-label={`Sinn ${index + 1} entfernen`}
                        onClick={() => senses.remove(index)}><Trash2/></Button>
                </div>
                <SenseNumberField name={`senses.specialSenses.${index}.rangeMeters`} label="Reichweite (m)"/>
                {values[index]?.type === "BLINDSIGHT" && <>
                    <SenseNumberField name={`senses.specialSenses.${index}.rangeWhenDeafMeters`} label="Reichweite bei Taubheit (m)" optional/>
                    <Controller control={control} name={`senses.specialSenses.${index}.blindOutsideRange`}
                        render={({field}) => <label className="flex items-center gap-2 text-xs">
                            <Checkbox checked={field.value} onCheckedChange={checked => field.onChange(checked === true)}/>
                            Blind außerhalb des Radius
                        </label>}/>
                </>}
            </div>)}
        </div>
    </section>;
}
